/**
 * Controller badge — injects a PASSIVE, non-interactive status indicator that
 * shows who is currently driving a headed browser session.
 *
 * Control is implicit and cooperative (see {@link module:session/user-takeover}):
 * the daemon infers "the user is driving" from real input events and defers the
 * agent automatically, then auto-resumes once the user goes idle. There is no
 * click-to-pause toggle. Consequently the badge:
 *
 *   - is `pointer-events:none` — it can never intercept a page click, and the
 *     agent's coordinate clicks pass straight through it,
 *   - is `aria-hidden` and carries a `data-canary-chrome` marker so it drops out
 *     of the agent's accessibility snapshots (it is chrome, not page content),
 *   - is pinned to the top-center, away from the bottom-right corner where
 *     pagination controls, chat widgets, and "back to top" buttons live.
 *
 * The badge reflects live "You're driving" state entirely client-side (a passive
 * capture-phase listener), so it needs no daemon round-trips.
 *
 * @module
 */

import type { PlaywrightClient } from '@chatsdet/browser-core';
import type { SessionController } from './types.js';

const BADGE_ID = '__canary_controller_badge__';

/** How long the badge shows "You're driving" after the last user input (ms). */
const DRIVING_FLASH_MS = 2500;

/** Shared container styles — passive, top-center, never intercepts input. */
const BADGE_CSS =
  'position:fixed;top:10px;left:50%;transform:translateX(-50%);' +
  'z-index:2147483647;pointer-events:none;user-select:none;' +
  'padding:4px 10px;border-radius:7px;' +
  'font:600 11px/1.2 system-ui,-apple-system,sans-serif;' +
  'display:flex;align-items:center;gap:6px;opacity:.55;color:#fff;' +
  'background:rgba(24,24,27,.72);box-shadow:0 1px 6px rgba(0,0,0,.25);';

/**
 * Script that creates the passive "who's driving" badge for an agent-controlled
 * headed session. Designed to work both via addInitScript (survives navigation)
 * and evaluate. Reads no server state — it flips to "You're driving" locally
 * whenever it observes real user input, then relaxes back to "Agent".
 */
export const PASSIVE_BADGE_SCRIPT = `(() => {
  function initBadge() {
    const BADGE_ID = '${BADGE_ID}';
    const DRIVING_FLASH_MS = ${DRIVING_FLASH_MS};

    let el = document.getElementById(BADGE_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = BADGE_ID;
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('data-canary-chrome', '1');
      el.style.cssText = '${BADGE_CSS}';
      document.body.appendChild(el);
    }

    function render(driving) {
      if (!el) return;
      var dot = driving ? '#4DA6FF' : '#FFBD0F';
      var label = driving ? "You're driving" : 'Agent';
      el.innerHTML =
        '<span style="width:7px;height:7px;border-radius:50%;background:' + dot + ';"></span>'
        + '<span>' + label + '</span>';
    }
    render(false);

    // Passive, capture-phase listeners: reflect user activity WITHOUT ever
    // calling preventDefault or intercepting the event. Purely cosmetic.
    if (!window.__canaryBadgeWired) {
      window.__canaryBadgeWired = true;
      var timer = null;
      var flash = function () {
        var b = document.getElementById(BADGE_ID);
        if (!b) return;
        render(true);
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { render(false); }, DRIVING_FLASH_MS);
      };
      ['pointerdown', 'keydown', 'input', 'wheel'].forEach(function (t) {
        try { document.addEventListener(t, flash, { capture: true, passive: true }); } catch {}
      });
    }

    // Re-inject if the page removes the badge (e.g. framework DOM cleanup).
    try {
      var observer = new MutationObserver(function () {
        if (!document.getElementById(BADGE_ID)) {
          observer.disconnect();
          initBadge();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch {}
  }

  // Wait for body to exist (addInitScript runs before DOM is ready).
  if (document.body) {
    initBadge();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBadge);
  } else {
    initBadge();
  }
})()`;

/**
 * Static, non-interactive label badge — used for cloud/CLI-forced control where
 * there is no user in the loop. Also passive (`pointer-events:none`, aria-hidden).
 */
function staticBadgeScript(controller: SessionController): string {
  const color = controller === 'cli' ? '#4DA6FF' : '#FFBD0F';
  const label = controller === 'cli' ? 'CLI' : 'Cloud';

  return `(() => {
    let el = document.getElementById('${BADGE_ID}');
    if (!el) {
      el = document.createElement('div');
      el.id = '${BADGE_ID}';
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('data-canary-chrome', '1');
      el.style.cssText = '${BADGE_CSS}';
      document.body.appendChild(el);
    }
    el.innerHTML =
      '<span style="width:7px;height:7px;border-radius:50%;background:${color};"></span>'
      + '<span>${label}</span>';
  })()`;
}

/**
 * Inject or update the controller badge in the browser page.
 *
 * For agent-controlled headed sessions (`interactive: true`) injects the passive
 * "who's driving" indicator. For cloud/CLI-forced control shows a static label.
 * Both are passive — neither can ever intercept a click.
 */
export async function updateControllerBadge(
  client: PlaywrightClient,
  controller: SessionController,
  options?: {
    interactive?: boolean;
    logger?: { debug?: (...args: unknown[]) => void };
  }
): Promise<void> {
  try {
    const interactive = options?.interactive ?? false;
    if (interactive) {
      await client.evaluate(PASSIVE_BADGE_SCRIPT);
    } else {
      await client.evaluate(staticBadgeScript(controller));
    }
  } catch (err) {
    options?.logger?.debug?.('[badge] updateControllerBadge failed', {
      controller,
      interactive: options?.interactive ?? false,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
